'use client';
import React from 'react';
import { lockBodyScroll } from '@/utils/scrollLock';

// BL-798: Zaius locks the page with `body { overflow: hidden }` (its
// `disablePageScrolling`), which makes <body> a scroll container. The sticky
// header then anchors to body's unscrolled scrollport instead of the viewport
// and disappears while the campaign modal is open. We strip that lock and apply
// ModalContext's position:fixed one, which the header survives.
const ZAIUS_MODAL_CONTAINER_ID = 'zmodal_parent_div';
// Zaius only injects the modal once the campaign content loads; if it never
// arrives (no campaign, blocked script) we stop watching rather than leak an observer.
const ZAIUS_MODAL_WAIT_MS = 10000;

let stopActiveWatcher = null;

const getZaiusBackdrop = () =>
  document.getElementById(ZAIUS_MODAL_CONTAINER_ID)?.parentElement ?? null;

const watchZaiusModal = () => {
  if (typeof document === 'undefined' || stopActiveWatcher) return;

  let unlockScroll = null;
  let waitTimeoutId = null;

  const stop = () => {
    observer.disconnect();
    clearTimeout(waitTimeoutId);
    unlockScroll?.();
    unlockScroll = null;
    stopActiveWatcher = null;
  };

  const observer = new MutationObserver(() => {
    if (getZaiusBackdrop()) {
      clearTimeout(waitTimeoutId);
      // Zaius re-applies its lock every time it re-measures the modal.
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
      unlockScroll ??= lockBodyScroll();

      return;
    }

    if (unlockScroll) stop();
  });

  observer.observe(document.body, {
    childList: true,
    attributes: true,
    attributeFilter: ['style'],
  });

  waitTimeoutId = setTimeout(() => {
    if (!getZaiusBackdrop()) stop();
  }, ZAIUS_MODAL_WAIT_MS);

  stopActiveWatcher = stop;
};

export const OpenZaiusModal = (campaignId) => {
  if (!campaignId) return;

  if (!window.zaius) {
    console.warn('Zaius not initialized yet');
    return;
  }

  const openModal = () => {
    try {
      window.zaius._tracker?.web?.hasShownModal?.(false);
      watchZaiusModal();
      window.zaius.dispatch('web', 'showContent', {
        contentId: campaignId,
        target: { selector: '', position: 'modal' },
      });
    } catch (err) {
      console.error('Error dispatching Zaius modal:', err);
    }
  };

  if (typeof window.zaius.dispatch === 'function') {
    openModal();
  } else {
    window.zaius.onload(openModal);
  }
};

// zaius_campaign_id not yet in CMS, using env var until field is added to utility_nav content type
export const openZaiusCampaign = () =>
  OpenZaiusModal(process.env.NEXT_PUBLIC_ZAIUS_CAMPAIGN_ID);

export const ZaiusModal = ({ campaignId, children }) => {
  if (!campaignId || !children) return null;

  return React.cloneElement(children, {
    onClick: () => OpenZaiusModal(campaignId),
  });
};
